/*
 * Copyright (c) 2022. Legalbird eine Marke der R&S Internet Jewels GmbH
 */
import _ from "lodash";
import { isValidDate } from "./validationRules";
import { getProductData } from "./Product/ProductService/productService";
import { translateSelectValues } from "./Translations/translatorService";
import { VAT_MULTIPLIER } from "./globalVars";
import { ValidatorType } from "../types/ValidatorType";
import { Customer } from "../types/Customer";
import { UseForm } from "../types/UseForm";
import { HydraEntity } from "../types/HydraEntity";
import { ProductUnion } from "../types/ProductUnion";
import { FormElementPropsWithoutProduct } from "../components/Case/CaseForm/FormElement";
import dayjs from "dayjs";

export const fileCheck = ({ value, files }: EventTarget & HTMLInputElement) => {
  let errorMsg = "";
  let hasError = false;
  if (!files) {
    return {
      hasError: true,
      errorMsg: "errorMsg",
    };
  }

  if (!checkFileFormat(value)) {
    hasError = true;
    errorMsg = "Ihre Datei hat leider das falsche Format. Erlaubte Dateiformate sind JPG, PNG und PDF.";
  }

  if (files[0].size > 10485760) {
    hasError = true;
    errorMsg = "Ihre Datei ist leider zu groß. Die maximale Dateigröße ist 10 MB.";
  }

  if (files[0].size === 0) {
    hasError = true;
    errorMsg = "Ihre Datei ist leer. Bitte kontrollieren Sie die Dateigröße.";
  }

  return {
    hasError: hasError,
    errorMsg: errorMsg,
  };
};

const checkFileFormat = (filename: string) => {
  const fileEnding = filename.slice(-5);
  return /(\.jpg)|(\.png)|(\.jpeg)|(\.pdf)/i.test(fileEnding);
};

export const formValue = (formData: Record<string, any>, path: string, defaultValue = "") => {
  return _.defaultTo(_.get(formData, path), defaultValue);
};

export const convertToFloat = (value: string) => {
  return parseFloat(value);
};

export const convertToFormattedTime = (timeString: string | Record<string, any> | null) => {
  if (!_.isString(timeString)) {
    return timeString;
  }
  const date = new Date(timeString);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${hours}:${minutes}`;
};

export const convertFromFloat = (value: number) => {
  if (!value && value !== 0) {
    return "";
  }
  return String(value);
};

export const toIban = (value: string) => {
  return "DE" + _.replace(value, /\s/g, "");
};

export const fromIban = (value: string) => {
  return _.trimStart(value, "DE");
};

export const fromEntityToIri = (value: HydraEntity) => {
  return (value && value["@id"]) || null;
};

export const convertBooleanToYesNoString = (value: boolean | null) => {
  if (value === false) {
    return "no";
  }
  if (value === true) {
    return "yes";
  }
  return "";
};

export const floatFields = [
  "costCoverageAmount",
  "incomeEmploymentAmount",
  "incomeSelfEmploymentAmount",
  "incomeRentAmount",
  "incomeAssetsAmount",
  "incomeChildAllowanceAmount",
  "incomeLivingAmount",
  "incomeAlimonyAmount",
  "incomePensionAmount",
  "incomeUnemploymentAllowanceAmount",
  "incomeUnemploymentAllowance2Amount",
  "incomeSicknessAmount",
  "incomeParentalAllowanceAmount",
  "incomeOther1Amount",
  "incomeOther2Amount",
  "taxAmount",
  "socialSecurityAmount",
  "otherInsuranceAmount",
  "mobilityCostAmount",
  "otherExpensesAmount",
  "realEstate1Value",
  "cashAmount",
  "basicRentOrRepayment",
  "heatingCosts",
  "additionalCosts",
  "costPaidByPetitioner",
];

export const numberFields = [
  "realEstate1Size",
  "realEstate1NumberApartments",
  "appartmentSize",
  "numberRooms",
  "numberRoommates",
];

export const prepareFormData = (processCostSupport: any) => {
  let resultData = _.merge({}, processCostSupport);

  if (resultData.petitioner.birthDate !== null && !isValidDate(resultData.petitioner.birthDate)) {
    resultData.petitioner.birthDate = dayjs(resultData.petitioner.birthDate).format("DD.MM.YYYY");
  }

  _.forEach(resultData, (value, key) => {
    if (_.isBoolean(value)) {
      resultData[key] = value ? "yes" : "no";
    }
    if (floatFields.includes(key)) {
      resultData[key] = convertFromFloat(value);
    }
    if (numberFields.includes(key)) {
      resultData[key] = value + "";
    }
  });

  return resultData;
};

export const hasNoIncome = (pcs: any) => {
  const incomeFields = [
    "hasIncomeEmployment",
    "hasIncomeSelfEmployment",
    "hasIncomeRent",
    "hasIncomeAssets",
    "hasIncomeChildAllowance",
    "hasIncomeLiving",
    "hasIncomeAlimony",
    "hasIncomePension",
    "hasIncomeUnemploymentAllowance",
    "hasIncomeUnemploymentAllowance2",
    "hasIncomeSickness",
    "hasIncomeParentalAllowance",
    "hasIncomeOther1",
    "hasIncomeOther2",
  ];

  return _.reduce(pcs, (result, value, key) => {
    if (!incomeFields.includes(key)) {
      return !!result;
    }
    return result && value !== "yes";
  });
};

export const alimonyClaimsAmount = (pcs: any) => {
  return _.reduce(
    pcs.alimonyPersons,
    (sum, person) => {
      return sum + convertToFloat(person.alimony);
    },
    0
  );
};

export const receiptsComplete = (processCostSupport: any) => {
  const hasReceipt = (receiptKey: string) => {
    return _.isArrayLike(processCostSupport.receipts[receiptKey]) && processCostSupport.receipts[receiptKey].length > 0;
  };

  if (processCostSupport.isCostCovered && !hasReceipt("pcs_receipt_b")) {
    return false;
  }

  if (processCostSupport.hasAlimonyClaims && !hasReceipt("pcs_receipt_c")) {
    return false;
  }

  if (
    processCostSupport.hasRelativesAlimonyClaims &&
    alimonyClaimsAmount(processCostSupport) > 0 &&
    !hasReceipt("pcs_receipt_d")
  ) {
    return false;
  }

  if (processCostSupport.hasSocialWelfare && !hasReceipt("pcs_receipt_social_welfare")) {
    return false;
  }

  if (processCostSupport.hasIncomeEmployment && !hasReceipt("pcs_receipt_e1")) {
    return false;
  }

  if (processCostSupport.hasIncomeSelfEmployment && !hasReceipt("pcs_receipt_e2")) {
    return false;
  }

  if (processCostSupport.hasIncomeRent && !hasReceipt("pcs_receipt_e3")) {
    return false;
  }

  if (processCostSupport.hasIncomeAssets && !hasReceipt("pcs_receipt_e4")) {
    return false;
  }

  if (processCostSupport.hasIncomeLiving && !hasReceipt("pcs_eceipt_e6")) {
    return false;
  }

  if (processCostSupport.hasIncomePension && !hasReceipt("pcs_receipt_e8")) {
    return false;
  }

  if (processCostSupport.hasIncomeUnemploymentAllowance && !hasReceipt("pcs_receipt_e9")) {
    return false;
  }

  if (processCostSupport.hasIncomeUnemploymentAllowance2 && !hasReceipt("pcs_receipt_e10")) {
    return false;
  }

  if (processCostSupport.hasIncomeSickness && !hasReceipt("pcs_receipt_e11")) {
    return false;
  }

  if (processCostSupport.hasIncomeParentalAllowance && !hasReceipt("pcs_receipt_e12")) {
    return false;
  }

  if (processCostSupport.hasIncomeOther1 && !hasReceipt("pcs_receipt_e13")) {
    return false;
  }

  if (processCostSupport.hasIncomeOther2 && !hasReceipt("pcs_receipt_e14")) {
    return false;
  }

  if (processCostSupport.hasOtherInsuranceDeduction && !hasReceipt("pcs_receipt_f3")) {
    return false;
  }

  if (
    processCostSupport.hasMobilityCostDeduction &&
    processCostSupport.mobilityCostUnit !== "km" &&
    !hasReceipt("pcs_receipt_f4")
  ) {
    return false;
  }

  if (processCostSupport.hasOtherExpensesDeduction && !hasReceipt("pcs_receipt_f5")) {
    return false;
  }

  if (processCostSupport.hasIncomeLiving === false && !hasReceipt("pcs_receipt_h1")) {
    return false;
  }

  if (processCostSupport.hasPensionInsurance && !hasReceipt("pcs_receipt_g5")) {
    return false;
  }

  if (processCostSupport.hasOtherAssets && !hasReceipt("pcs_receipt_g6")) {
    return false;
  }

  if (processCostSupport.hasPropertyLoans) {
    for (let i = 1; i <= processCostSupport.propertyLoans.length; i++) {
      if (!hasReceipt("pcs_receipt_h6" + i)) {
        return false;
      }
    }
  }

  if (processCostSupport.hasOtherPaymentObligations) {
    for (let i = 1; i <= processCostSupport.paymentObligations.length; i++) {
      if (!hasReceipt("pcs_receipt_i" + i)) {
        return false;
      }
    }
  }

  if (processCostSupport.hasSpecialFinancialBurdens) {
    for (let i = 1; i <= processCostSupport.specialFinancialBurdens.length; i++) {
      if (!hasReceipt("pcs_receipt_j" + i)) {
        return false;
      }
    }
  }

  if (processCostSupport.hasBankAccount) {
    for (let i = 1; i <= processCostSupport.bankAccounts.length; i++) {
      if (!hasReceipt("pcs_receipt_g1" + i)) {
        return false;
      }
    }
  }

  return true;
};

const getValidator = async (validator: ValidatorType, fieldName: string, values: Record<string, any>) => {
  const value = _.get(values, fieldName);
  if (validator.relatedField) {
    if (validator.relatedField === "allValues") {
      return await validator.validator(value, values);
    }
    if (!_.has(values, validator.relatedField)) {
      //so that the error is not shown on onblur
      return true;
    }
    return await validator.validator(value, _.get(values, validator.relatedField));
  }

  if (_.has(validator, "params")) {
    return await validator.validator(value, ...validator.params);
  }
  return await validator.validator(value);
};

export const formValidate = async (values: Record<string, any>, validators: Record<string, ValidatorType[]>) => {
  let errors: Record<string, any> = {};
  let fieldName;
  for (fieldName in validators) {
    if (_.has(validators, fieldName) && _.has(values, fieldName)) {
      errors[fieldName] = await fieldValidate(fieldName, validators[fieldName], values);
    }
  }
  return errors;
};

const fieldValidate = async (fieldName: string, fieldValidators: ValidatorType[], values: Record<string, any>) => {
  for (let i = 0; i < fieldValidators.length; i++) {
    const isValid = await getValidator(fieldValidators[i], fieldName, values);
    if (!isValid) {
      return fieldValidators[i].message;
    }
  }
  return null;
};

export const getErrors = (e: any) => {
  let errors: Record<string, any> = {};
  _.forEach(e, (error, key) => {
    if (!!error) {
      errors[key] = error;
    }
  });
  return errors;
};

const formats = {
  date: "YYYY-MM-DD",
  dateTime: "DD.MM.YYYY HH:mm",
};

const customerDateFields: Array<{ path: string; type: keyof typeof formats }> = [
  {
    path: "verification",
    type: "dateTime",
  },
];

export const prepareFormCustomerDates = (customer: Customer) => {
  let resultData = _.merge({}, customer);
  _.forEach(customerDateFields, (dateField) => {
    const dateValue = _.get(customer, dateField.path);
    if (dateValue) {
      _.set(resultData, dateField.path, dayjs(_.get(customer, dateField.path)).format(formats[dateField.type]));
    }
  });
  return resultData;
};

export const insurers = [
  {
    name: "Adam Riese GmbH Rechtsschutz",
    label: "Adam Riese",
    id: 1,
  },
  {
    name: "ADVOCARD Rechtsschutzversicherung AG",
    label: "ADVOCARD",
    id: 2,
  },
  {
    name: "Aeguron - CHECK24 Vergleichsportal für Sachversicherungen GmbH",
    label: "Aeguron",
    id: 3,
  },
  {
    name: "ALLRECHT Rechtsschutzversicherungen",
    label: "ALLRECHT",
    id: 4,
  },
  {
    name: "ADAC Versicherung AG",
    label: "ADAC",
    id: 5,
  },
  {
    name: "ARAG SE",
    label: "ARAG",
    id: 6,
  },
  {
    name: "Badische Rechtsschutzversicherung AG",
    label: "Badische Rechtsschutzversicherung",
    id: 7,
  },
  {
    name: "BavariaDirekt eine Marke der OVAG - Ostdeutsche Versicherung Aktiengesellschaft",
    label: "BavariaDirekt",
    id: 8,
  },
  {
    name: "ConceptIF",
    label: "ConceptIF",
    id: 9,
  },
  {
    name: "DEURAG Deutsche Rechtsschutz-Versicherung AG",
    label: "DEURAG",
    id: 10,
  },
  {
    name: "DEVK Rechtsschutz-Versicherungs-AG",
    label: "DEVK",
    id: 11,
  },
  {
    name: "DMB Rechtsschutz-Versicherung AG",
    label: "DMB",
    id: 12,
  },
  {
    name: "ERGO Versicherung AG",
    label: "ERGO",
    id: 13,
  },
  {
    name: "IDEAL Versicherung AG",
    label: "IDEAL",
    id: 14,
  },
  {
    name: "Medien-Versicherung a.G.",
    label: "Medien-Versicherung",
    id: 15,
  },
  {
    name: "R+V Versicherung AG",
    label: "R+V Versicherung",
    id: 16,
  },
  {
    name: "ROLAND Rechtsschutz-Versicherungs-AG",
    label: "ROLAND Rechtsschutz",
    id: 17,
  },
  {
    name: "DA Deutsche Allgemeine Versicherung Aktiengesellschaft",
    label: "DA Deutsche Allgemeine Versicherung",
    id: 18,
  },
  {
    name: "Debeka Allgemeine Versicherung AG",
    label: "Debeka",
    id: 19,
  },
  {
    name: "WGV Rechtsschutz-Schadenservice GmbH",
    label: "WGV",
    id: 20,
  },
  {
    name: "VGH Versicherungen",
    label: "VGH",
    id: 21,
  },
  {
    name: "Continentale Sachversicherung AG",
    label: "Continentale",
    id: 22,
  },
  {
    name: "HDI Versicherung AG",
    label: "HDI",
    id: 23,
  },
  {
    name: "AUXILIA Rechtsschutz-Versicherungs-AG",
    label: "AUXILIA",
    id: 24,
  },
  {
    name: "Concordia Versicherungs-Gesellschaft a.G.",
    label: "Concordia",
    id: 25,
  },
  {
    name: "D.A.S. Rechtsschutz Leistungs-GmbH",
    label: "D.A.S. Rechtsschutz",
    id: 26,
  },
  {
    name: "HUK-COBURG-Rechtsschutzversicherung",
    label: "HUK-COBURG",
    id: 27,
  },
  {
    name: "Jurpartner Services, Gesellschaft für Rechtsschutz-Schadenregulierung mbH",
    label: "Jurpartner Services",
    id: 28,
  },
  {
    name: "LVM Verischerung",
    label: "LVM",
    id: 29,
  },
  {
    name: "Neue Rechtsschutz Versicherung AG",
    label: "Neue Rechtsschutz Versicherung",
    id: 30,
  },
  {
    name: "Örag Rechtsschutzversicherungs AG",
    label: "Örag",
    id: 31,
  },
  {
    name: "Rechtsschutz Union Schaden GmbH",
    label: "Rechtsschutz Union Schaden",
    id: 32,
  },
  {
    name: "RSS Rechtsschutz-Service GmbH",
    label: "RSS Rechtsschutz-Service",
    id: 33,
  },
  {
    name: "Zurich Rechtsschutz-Schadenservice GmbH",
    label: "Zurich Rechtsschutz",
    id: 34,
  },
  {
    name: "SIGNAL IDUNA Allgemeine Versicherung AG",
    label: "SIGNAL IDUNA",
    id: 35,
  },
  {
    name: "Allianz Versicherungs-AG",
    label: "Allianz",
    id: 36,
  },
  {
    name: "Württembergische Rechtsschutzversicherung",
    label: "Württembergische Rechtsschutzversicherung",
    id: 37,
  },
  {
    name: "Verti Versicherung AG",
    label: "Verti",
    id: 40,
  },
  {
    name: "Sparkassen DirektVersicherung AG",
    label: "Sparkassen DirektVersicherung",
    id: 41,
  },
  {
    name: "Mecklenburgische Versicherungsgruppe",
    label: "Mecklenburgische",
    id: 42,
  },
  {
    name: "VRK Sachversicherung AG",
    label: "VRK Versicherung / Bruderhilfe",
    id: 43,
  },
  {
    name: "Landesschadenhilfe Versicherung",
    label: "Landesschadenhilfe",
    id: 44,
  },
  {
    name: "Itzehoer Versicherung",
    label: "Itzehoer",
    id: 45,
  },
  {
    name: "Provinzial",
    label: "Provinzial",
    id: 46,
  },
  {
    name: "Nürnberger Versicherung",
    label: "Nürnberger",
    id: 48,
  },
  {
    name: "GetSafe",
    label: "GetSafe",
    id: 49,
  },
  {
    name: "DFV Deutsche Familienversicherung AG",
    label: "DFV Deutsche Familienversicherung",
    id: 50,
  },
  {
    name: "VPV Allgemeine Versicherungs-AG",
    label: "VPV Allgemeine Versicherungs-AG",
    id: 51,
  },
  {
    name: "DOMCURA AG",
    label: "DOMCURA",
    id: 52,
  },
  {
    name: "AIA AG",
    label: "AIA",
    id: 53,
  },
  {
    name: "Alte Leipziger",
    label: "Alte Leipziger",
    id: 54,
  },
];

export const transformDayjsToStrings = (
  product: UseForm["values"],
  productClassName: ProductUnion["productClassName"]
) => {
  let resultData = _.merge({}, product);
  _.forEach(getProductData(productClassName, "formTransformations.dateFields"), (dateField) => {
    if (dateField.type === "date" && _.has(product, dateField.path) && _.get(product, dateField.path)) {
      _.set(resultData, dateField.path, dayjs(_.get(product, dateField.path)).format(formats.date));
    }
    /*if (dateField.type === "dateTime" && _.has(product, dateField.path) && _.get(product, dateField.path)) {
              _.set(resultData, dateField.path, dayjs(_.get(product, dateField.path)).format(formats.dateTime));
            }*/
  });
  return resultData;
};

export const transformToFormValues = (product: ProductUnion) => {
  let resultData = _.merge({}, product);

  const dateFieldTransformations: Array<{
    type: "date" | "dateTime";
    path: string;
  }> = getProductData(product.productClassName, "formTransformations.dateFields");
  _.forEach(dateFieldTransformations, (dateField) => {
    if (_.has(product, dateField.path) && _.get(product, dateField.path)) {
      _.set(resultData, dateField.path, dayjs(_.get(product, dateField.path)).format(formats[dateField.type]));
    }
  });

  _.forEach(getProductData(product.productClassName, "formTransformations.floatFields"), (floatFieldPath) => {
    if (_.has(product, floatFieldPath) && typeof _.get(product, floatFieldPath) === "number") {
      _.set(resultData, floatFieldPath, convertFromFloat(_.get(product, floatFieldPath)));
    }
  });

  _.forEach(getProductData(product.productClassName, "formTransformations.numberFields"), (numberFieldPath) => {
    if (_.has(product, numberFieldPath)) {
      _.set(resultData, numberFieldPath, _.toString(_.get(product, numberFieldPath)));
    }
  });

  _.forEach(
    getProductData(product.productClassName, "formTransformations.extendedSelectFields"),
    (extendedSelectPath) => {
      if (_.has(product, extendedSelectPath)) {
        let value = _.get(product, extendedSelectPath, {});
        if (JSON.stringify(value) === "[]") {
          value = {};
        }
        _.set(resultData, extendedSelectPath, JSON.stringify(value));
      }
    }
  );

  _.forEach(getProductData(product.productClassName, "formTransformations.ibanFields"), (ibanFieldPath) => {
    if (_.has(product, ibanFieldPath)) {
      _.set(resultData, ibanFieldPath, fromIban(_.get(product, ibanFieldPath)));
    }
  });

  _.forEach(getProductData(product.productClassName, "formTransformations.entityIriFields"), (entityPath) => {
    if (_.has(product, entityPath)) {
      _.set(resultData, entityPath, fromEntityToIri(_.get<HydraEntity>(product, entityPath)));
    }
  });

  _.forEach(getProductData(product.productClassName, "formTransformations.timeFields"), (timeFieldPath) => {
    if (_.has(product, timeFieldPath) && _.get(product, timeFieldPath)) {
      const timeString = _.get(product, timeFieldPath);
      const resultTime = convertToFormattedTime(timeString);
      _.set(resultData, timeFieldPath, resultTime);
    }
  });
  return resultData;
};

export const transformToFormattedValue = (product: ProductUnion, path: string) => {
  let value = _.get(product, path);

  if (!value) {
    return "[Wert für Platzhalter nicht befüllt]";
  }

  if (_.includes(getProductData(product.productClassName, "formTransformations.floatFields"), path)) {
    return convertFromFloat(value);
  }

  if (_.includes(getProductData(product.productClassName, "formTransformations.numberFields"), path)) {
    return _.toString(value);
  }

  if (_.includes(getProductData(product.productClassName, "formTransformations.timeFields"), path)) {
    return convertToFormattedTime(value);
  }

  if (_.includes(getProductData(product.productClassName, "formTransformations.ibanFields"), path)) {
    return fromIban(value);
  }

  if (
    _.some(
      getProductData(product.productClassName, "formTransformations.dateFields"),
      (dateField) => dateField.path === path
    )
  ) {
    return dayjs(value).format("DD.MM.YYYY");
  }

  return translateSelectValues(path, value, product.productClassName);
};

export const transformFromFormValues = (
  productClassName: ProductUnion["productClassName"],
  values: Record<string, any>
) => {
  let resultData = _.merge({}, values);

  _.forEach(getProductData(productClassName, "formTransformations.floatFields"), (floatFieldPath) => {
    if (_.has(values, floatFieldPath) && _.get(values, floatFieldPath)) {
      _.set(resultData, floatFieldPath, convertToFloat(_.get(values, floatFieldPath)));
    }
  });

  _.forEach(getProductData(productClassName, "formTransformations.numberFields"), (numberFieldPath) => {
    if (_.has(values, numberFieldPath)) {
      _.set(resultData, numberFieldPath, _.toNumber(_.get(values, numberFieldPath)));
    }
  });

  _.forEach(getProductData(productClassName, "formTransformations.extendedSelectFields"), (extendedSelectPath) => {
    if (_.has(values, extendedSelectPath)) {
      _.set(resultData, extendedSelectPath, JSON.parse(_.get(values, extendedSelectPath)));
    }
  });

  _.forEach(getProductData(productClassName, "formTransformations.dateFields"), (dateFieldPath) => {
    if (_.has(values, dateFieldPath.path) && _.get(values, dateFieldPath.path) === "") {
      _.set(resultData, dateFieldPath.path, null);
    }
  });

  _.forEach(getProductData(productClassName, "formTransformations.timeFields"), (timeFieldPath) => {
    if (_.has(values, timeFieldPath) && _.get(values, timeFieldPath) === "") {
      _.set(resultData, timeFieldPath, null);
    }
  });

  _.forEach(getProductData(productClassName, "formTransformations.ibanFields"), (fieldPath) => {
    if (_.has(values, fieldPath) && _.get(values, fieldPath)) {
      _.set(resultData, fieldPath, toIban(_.get(values, fieldPath)));
    }
  });
  return resultData;
};

export const getMediaObjectSize = (bytes: number) => {
  if (!bytes) {
    return "unbekannt";
  }

  let divisionCount = 0;
  let suffix;
  let size = bytes;
  while (size / 1000 > 1) {
    size = size / 1000;
    divisionCount++;
  }

  switch (divisionCount) {
    case 0:
      suffix = "byte";
      break;
    case 1:
      suffix = "KB";
      break;
    case 2:
      suffix = "MB";
      break;
    case 3:
      suffix = "GB";
      break;
    default:
      suffix = "byte";
      break;
  }
  return parseFloat(size.toFixed(1)) + " " + suffix;
};

export const nl2br = (text: string, isXhtml = true) => {
  const breakTag = isXhtml ? "<br/>" : "<br>";
  return (text + "").replace(/([^>\r\n]?)(\r\n|\n\r|\r|\n)/g, "$1" + breakTag + "$2");
};

export const arrayToSelectOptions = (array: string[]) => {
  let preparedArray: Array<{ value: string }> = [];
  _.map(array, (item) => {
    preparedArray.push({ value: item });
  });
  return preparedArray;
};

export const grossValueToNet = (value: number) => {
  return +(value / VAT_MULTIPLIER).toFixed(2);
};

export function insertAfterPath<T extends { path?: string }>(elements: T[], newElements: T[], afterPath: string): T[] {
  const index = elements.findIndex((el) => el.path === afterPath);

  if (index === -1) {
    return [...elements, ...newElements];
  }

  return [...elements.slice(0, index + 1), ...newElements, ...elements.slice(index + 1)];
}

type Field<T extends ProductUnion = ProductUnion> = FormElementPropsWithoutProduct<T>;

type FieldPatch<T extends ProductUnion = ProductUnion> = Partial<Omit<Field<T>, "path">>;

export function removeByPath<T extends { path?: string }>(elements: T[], pathToRemove: string): T[] {
  const index = elements.findIndex((el) => el.path === pathToRemove);

  if (index === -1) {
    return elements;
  }

  return [...elements.slice(0, index), ...elements.slice(index + 1)];
}

type FieldOverride<T extends ProductUnion = ProductUnion> =
  | { path: string; patch: FieldPatch<T> }
  | { match: RegExp | ((field: Field<T>) => boolean); patch: FieldPatch<T> };

function cloneField<T extends ProductUnion = ProductUnion>(field: Field<T>): Field<T> {
  return {
    ...field,
    alert: field.alert ? { ...field.alert } : field.alert,
    additionalProps: field.additionalProps ? { ...field.additionalProps } : field.additionalProps,
    validators: field.validators ? [...field.validators] : field.validators,
    mandatoryFields: field.mandatoryFields ? [...field.mandatoryFields] : field.mandatoryFields,
  };
}

function applyPatch<T extends ProductUnion = ProductUnion>(base: Field<T>, patch: FieldPatch<T>): Field<T> {
  return {
    ...base,
    ...patch,
    alert: base.alert || patch.alert ? { ...(base.alert ?? {}), ...(patch.alert ?? {}) } : base.alert,
    additionalProps:
      base.additionalProps || patch.additionalProps
        ? { ...(base.additionalProps ?? {}), ...(patch.additionalProps ?? {}) }
        : base.additionalProps,
  };
}

function matchesOverride<T extends ProductUnion = ProductUnion>(field: Field<T>, o: FieldOverride<T>): boolean {
  if ("path" in o) {
    return field.path === o.path;
  }
  if (o.match instanceof RegExp) {
    return o.match.test(field.path);
  }
  return o.match(field);
}

export function cloneFieldsWithOverrides<T extends ProductUnion = ProductUnion>(
  fields: Field<T>[],
  overrides: FieldOverride<T>[]
): Field<T>[] {
  return fields.map((field) => {
    let next = cloneField<T>(field);

    for (const o of overrides) {
      if (matchesOverride<T>(next, o)) {
        next = applyPatch(next, o.patch);
      }
    }

    return next;
  });
}
